import React, { useContext, useEffect, useState } from "react"
import { useIntl } from "react-intl"
import { useFiltersContext } from "components/Shared/GlobalFilter/context/filtersContext"
import PenetrationInfoPanel from "components/Transactional/Performance/components/PenetrationInfo/PenetrationInfoPanel"
import { InfoButton } from "components/Shared/InformationPanel"
import { mapGlobalFilters } from "components/Shared/GlobalFilter/utils"
import { globalFiltersMapBackendConfig } from "components/Shared/GlobalFilter/constants"
import NavTabs from "components/Shared/NavTabs"
import * as paths from "constants/paths"
import { isFeatureEnabled } from "config/feature-flags"
import BenchmarkTab from "components/Transactional/Performance/components/BenchmarkTab"
import SelectPenetration from "components/Transactional/Performance/components/SelectPenetration/SelectPenetration"
import "./benchmarkPenetration.scss"
import { COUNTY, REGION, STORE } from "constants/parameters"
import PenetrationSummary from "./PenetrationSummary"
import { PENETRATION_SUMMARY_TITLE_ID } from "components/Transactional/Performance/components/SummaryTable/SummaryTable.consts"
import {
	formatInsightSubtitle,
	generateInsightTitle,
	getLocationLevel,
	getTabTitle,
	getSubtitleByFilters,
} from "components/Transactional/Performance/utils/utils"
import {
	BENCHMARK_TABLE_TITLE_ID,
	COLUMN_NAMES,
	METRICS,
	SELECT_PENETRATION_SUBTITLE,
	SELECT_PENETRATION_TITLE,
	TIMELINE_DOUBLE_AXIS_COMPONENT_FEATURE_FLAG_ID,
	textBenchmarkByBrand,
	textBenchmarkByChannel,
	textBenchmarkByFormat,
	textBenchmarkByLocation,
	textTotalSalesTab,
} from "components/Transactional/Performance/constants"
import {
	BenchmarkTemplateProps,
	LevelName,
} from "components/Transactional/Performance/interfaces"
import RadioButtonPenetrationSelector from "components/Shared/RadioButtonPenetrationSelector"
import { PENETRATION } from "./constants"
import { ShowAllCustomerAlertContext } from "components/Transactional/Performance"
import Timeline from "components/Transactional/Performance/components/Timeline"

const BenchmarkPenetrationTemplate = (props: BenchmarkTemplateProps) => {
	const {
		period,
		dateInterval,
		levelName,
		metricName,
		onChangeLevelName,
		filterLevelSelected,
	} = props
	const setShowAllCustomerAlert = useContext(ShowAllCustomerAlertContext)
	const intl = useIntl()
	const {
		filters: globalFilters,
		moduleState,
		setModuleState,
	} = useFiltersContext()
	const {
		transactional: {
			penetration: { metric: penetrationMetric, timeline, summary },
		},
	} = moduleState
	const [isOpen, setIsOpen] = useState(false)

	const benchmarkGlobalFilters = mapGlobalFilters(
		globalFilters,
		globalFiltersMapBackendConfig
	)

	const filtersBenchmark = {
		...benchmarkGlobalFilters,
		period,
		metricName: timeline,
		...dateInterval,
	}

	const getPenetrationMetric = (metric: string) => {
		return `${metric}_${penetrationMetric}_${PENETRATION}`
	}

	const changeSummaryPenetrationType = (type: string) => {
		setShowAllCustomerAlert(type === penetrations[1].value)
		setModuleState({
			...moduleState,
			transactional: {
				...moduleState.transactional,
				penetration: {
					...moduleState.transactional.penetration,
					summary: type,
				},
			},
		})
	}

	useEffect(() => () => setShowAllCustomerAlert(false), [])

	const penetrations = [
		{
			disabled: false,
			value: "clients",
		},
		{
			disabled: false,
			value: "transactions",
		},
	]

	const generateTabComponent = (
		metricName: string,
		levelName: LevelName,
		hasPaginator: boolean
	) => {
		return (
			<BenchmarkTab
				filtersBenchmark={filtersBenchmark}
				setLevelName={onChangeLevelName}
				levelName={levelName}
				metricName={metricName}
				hasPaginator={hasPaginator}
				isPenetrationMetric={true}
			/>
		)
	}
	const generateBenchmarkTabs = () => {
		return [
			{
				text: getTabTitle(textTotalSalesTab, levelName, intl),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_TOTAL_SALES,
				component: () =>
					generateTabComponent(
						getPenetrationMetric(METRICS.BY_LEVEL),
						levelName,
						true
					),
			},
			{
				text: getTabTitle(textBenchmarkByBrand, levelName, intl),
				pathname: paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_BRANDS,
				component: () =>
					generateTabComponent(
						getPenetrationMetric(METRICS.BY_BRAND),
						COLUMN_NAMES.BRAND,
						true
					),
			},
			{
				text: getTabTitle(textBenchmarkByChannel, levelName, intl),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_CHANNELS,
				component: () =>
					generateTabComponent(
						getPenetrationMetric(METRICS.BY_CHANNEL),
						COLUMN_NAMES.CHANNEL,
						false
					),
			},
			{
				text: getTabTitle(textBenchmarkByFormat, levelName, intl),
				pathname: paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_FORMATS,
				component: () =>
					generateTabComponent(
						getPenetrationMetric(METRICS.BY_FORMAT),
						COLUMN_NAMES.FORMAT,
						false
					),
			},
			{
				text: getTabTitle(
					textBenchmarkByLocation,
					levelName,
					intl,
					filtersBenchmark
				),
				pathname:
					paths.ROOT_CONTEXT + paths.TRANSACTIONAL_BENCHMARK_BY_LOCATION,
				component: () =>
					generateTabComponent(
						getPenetrationMetric(METRICS.BY_LOCATION),
						getLocationLevel(
							benchmarkGlobalFilters[REGION],
							benchmarkGlobalFilters[COUNTY],
							benchmarkGlobalFilters[STORE]
						),
						true
					),
				active: isFeatureEnabled("benchmarkLocationTab"),
			},
		]
	}

	const title = generateInsightTitle(
		BENCHMARK_TABLE_TITLE_ID,
		levelName,
		getSubtitleByFilters(filterLevelSelected, intl),
		intl,
		metricName
	)

	const renderSubTitle = () => {
		const subTitle = formatInsightSubtitle(
			levelName,
			getSubtitleByFilters(filterLevelSelected, intl),
			period,
			intl,
			metricName
		)
		return <div className="sub-title">{subTitle}</div>
	}

	return (
		<>
			<div className="penetration-title">
				<div className="info-title">
					<h1>{intl.formatMessage({ id: PENETRATION_SUMMARY_TITLE_ID })}</h1>
					<InfoButton setIsOpen={setIsOpen} isOpen={isOpen} />
				</div>

				<div className="penetration-title-radiobuttons">
					<RadioButtonPenetrationSelector
						onSelectedPenetrationChanged={changeSummaryPenetrationType}
						selectedOption={summary}
						penetrations={penetrations}
						type="summary"
					/>
				</div>
			</div>
			<div className="penetration-summary">
				<PenetrationSummary
					filtersBenchmark={filtersBenchmark}
					summaryPenetrationType={summary}
					isVisible={isFeatureEnabled("penetrationSummary")}
				/>
			</div>
			<div className="penetration-tit">
				<div className="info-title">
					<h1>{intl.formatMessage({ id: SELECT_PENETRATION_TITLE })}</h1>
				</div>
				<div className="sub-title">
					{intl.formatMessage({ id: SELECT_PENETRATION_SUBTITLE })}
				</div>
			</div>
			<div className="penetration-filters">
				<SelectPenetration
					value={penetrationMetric}
					onPenetrationChange={(value: string) =>
						setModuleState({
							...moduleState,
							transactional: {
								...moduleState.transactional,
								penetration: {
									...moduleState.transactional.penetration,
									metric: value,
								},
							},
						})
					}
				/>
				<RadioButtonPenetrationSelector
					onSelectedPenetrationChanged={(value: string) =>
						setModuleState({
							...moduleState,
							transactional: {
								...moduleState.transactional,
								penetration: {
									...moduleState.transactional.penetration,
									timeline: value,
								},
							},
						})
					}
					selectedOption={timeline}
					penetrations={penetrations}
				/>
			</div>
			<Timeline
				level={levelName}
				template={"penetration_growth"}
				isVisible={false}
			/>
			<Timeline
				level={levelName}
				template={"penetration_double"}
				isVisible={isFeatureEnabled(
					TIMELINE_DOUBLE_AXIS_COMPONENT_FEATURE_FLAG_ID
				)}
			/>
			<NavTabs
				title={title}
				subtitleContent={renderSubTitle()}
				tabs={generateBenchmarkTabs()}
			/>
			<PenetrationInfoPanel setIsOpen={setIsOpen} isOpen={isOpen} />
		</>
	)
}

export default BenchmarkPenetrationTemplate
